/*! @wharfkit/protocol-scatter v4.0.1 | BSD-3-Clause | Copyright (c) 2023 Greymass Inc. | https://github.com/wharfkit/js */
import { a as __toESM } from "./rolldown-runtime.js";
import { Canceled, Chains, Checksum256, PermissionLevel, Serializer, SigningRequest, Transaction } from "@wharfkit/session";
//#region src/index.ts
async function getScatter(context) {
	if (typeof window === "undefined") throw new Error("The Scatter protocol requires a browser environment and cannot be used in Node.js.");
	const [{ Api, JsonRpc }, { ScatterEOS, ScatterJS }] = await Promise.all([import("eosjs"), import("./scatter.js").then((m) => __toESM(m.default))]);
	ScatterJS.plugins(new ScatterEOS());
	const url = new URL(context.chain.url);
	const protocol = url.protocol.replace(":", "") === "https" ? "https" : "http";
	const network = ScatterJS.Network.fromJson({
		blockchain: context.chain.name,
		chainId: String(context.chain.id),
		host: url.hostname,
		port: url.port ? Number(url.port) : protocol === "https" ? 443 : 80,
		protocol
	});
	if (!await ScatterJS.connect(context.appName, { network })) throw new Error("Unable to connect with Scatter wallet");
	const rpc = new JsonRpc(network.fullhost());
	rpc.getRequiredKeys = async () => [];
	return {
		scatter: ScatterJS,
		connector: ScatterJS.eos(network, Api, { rpc })
	};
}
async function handleLogin(context) {
	if (!context.ui) throw new Error("No UI available");
	const { scatter } = await getScatter(context);
	const scatterIdentity = await scatter.login();
	if (!scatterIdentity || !scatterIdentity.accounts) throw new Error("Unable to retrieve account from Scatter");
	const account = scatterIdentity.accounts[0];
	let chainId;
	if (account.chainId) chainId = account.chainId;
	else if (account.blockchain && Object.keys(Chains).includes(account.blockchain)) chainId = Chains[account.blockchain].id;
	else throw new Error(`Unknown chain: ${account.blockchain}`);
	return {
		chain: Checksum256.from(chainId),
		permissionLevel: PermissionLevel.from(`${account.name}@${account.authority}`)
	};
}
async function handleLogout(context) {
	if (context.session === void 0) throw new Error("Unknown session");
	const { scatter } = await getScatter({
		appName: context.appName,
		chain: context.session.chain
	});
	await scatter.logout();
}
async function handleSignatureRequest(resolved, context) {
	if (!context.ui) throw new Error("No UI available");
	const { scatter, connector } = await getScatter(context);
	const currentIdentity = await scatter.checkLogin();
	if (!currentIdentity || !currentIdentity.accounts) throw new Error("Please login first");
	const encoded = Serializer.encode({ object: resolved.transaction });
	const decoded = await connector.deserializeTransactionWithActions(encoded.array);
	const response = await connector.transact(decoded, { broadcast: false });
	if (!response.serializedTransaction) throw new Canceled("User Canceled request");
	const modified = Serializer.decode({
		data: response.serializedTransaction,
		type: Transaction
	});
	const modifiedRequest = await SigningRequest.create({ transaction: modified }, context.esrOptions);
	const abis = await modifiedRequest.fetchAbis(context.abiCache);
	const modifiedResolved = modifiedRequest.resolve(abis, context.permissionLevel);
	return {
		signatures: response.signatures,
		resolved: modifiedResolved
	};
}
//#endregion
export { getScatter, handleLogin, handleLogout, handleSignatureRequest };

//# sourceMappingURL=index.js.map